import * as React from "react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";
import { getSafeLinkTokens } from "../../lib/linkifyText";
import { mentionLookup, splitMentionTokens } from "../../lib/mentions";
import UserLink from "../UserLink";

/**
 * Shared typography for compose fields (textarea).
 * Display uses {@link FormattedBody}, which spaces hard breaks separately from wraps.
 */
export const FORMATTED_BODY_CLASS =
  "whitespace-pre-wrap break-words leading-normal";

/** Default autolink styles (theme tokens). */
export const FORMATTED_BODY_LINK_CLASS =
  "break-all rounded-sm underline underline-offset-2 text-accent hover:text-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring";

/**
 * Autolink styles on accent / sent-message backgrounds so links stay visible.
 * Merge with {@link FORMATTED_BODY_LINK_CLASS} via `linkClassName`.
 */
export const FORMATTED_BODY_LINK_ON_ACCENT_CLASS =
  "text-inherit hover:text-inherit decoration-current decoration-2";

/** Default @mention styles (theme tokens). */
export const FORMATTED_BODY_MENTION_CLASS =
  "rounded-sm font-medium text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring";

/**
 * Mention styles on accent / sent-message backgrounds.
 * Merge with {@link FORMATTED_BODY_MENTION_CLASS} via `mentionClassName`.
 */
export const FORMATTED_BODY_MENTION_ON_ACCENT_CLASS =
  "text-inherit hover:text-inherit";

/**
 * @param {Object} props
 * @param {{ type: string, value: string, href?: string, to?: string }} props.token
 * @param {string} props.className
 * @returns {JSX.Element}
 */
function LinkToken({ token, className }) {
  const stopBubble = (event) => {
    event.stopPropagation();
  };

  if (token.type === "internal" && token.to) {
    return (
      <Link to={token.to} className={className} onClick={stopBubble}>
        {token.value}
      </Link>
    );
  }

  if (token.type === "email") {
    return (
      <a href={token.href} className={className} onClick={stopBubble}>
        {token.value}
      </a>
    );
  }

  return (
    <a
      href={token.href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={className}
      onClick={stopBubble}
    >
      {token.value}
    </a>
  );
}

/**
 * @param {object} mentionToken
 * @param {Map<string, object>} lookup
 * @param {string} className
 * @returns {JSX.Element}
 */
function MentionToken({ mentionToken, lookup, className }) {
  const stopBubble = (event) => {
    event.stopPropagation();
  };
  const key =
    mentionToken.kind === "all"
      ? "all"
      : `${mentionToken.kind}:${mentionToken.id}`;
  const meta = lookup.get(key);
  const label = `@${meta?.name || (mentionToken.kind === "all" ? "all" : mentionToken.kind)}`;

  if (mentionToken.kind === "all") {
    return <span className={className}>{label}</span>;
  }

  return (
    <UserLink
      userId={mentionToken.id}
      isOrg={mentionToken.kind === "org"}
      className={className}
    >
      <span onClick={stopBubble}>{label}</span>
    </UserLink>
  );
}

/**
 * @param {string} text
 * @param {string} linkClassName
 * @param {string} mentionClassName
 * @param {Map<string, object>} lookup
 * @param {number} lineIndex
 * @returns {React.ReactNode}
 */
function linkifyLine(text, linkClassName, mentionClassName, lookup, lineIndex) {
  const mentionParts = splitMentionTokens(text);
  if (mentionParts.length === 0) {
    return "\u00A0";
  }
  return mentionParts.map((part, partIndex) => {
    const partKey = `${lineIndex}-${partIndex}`;
    if (part.type === "mention") {
      return (
        <MentionToken
          key={partKey}
          mentionToken={part}
          lookup={lookup}
          className={mentionClassName}
        />
      );
    }
    return getSafeLinkTokens(part.value).map((token, tokenIndex) => {
      const tokenKey = `${partKey}-${tokenIndex}`;
      if (token.type === "text") {
        return (
          <React.Fragment key={tokenKey}>{token.value}</React.Fragment>
        );
      }
      return (
        <LinkToken key={tokenKey} token={token} className={linkClassName} />
      );
    });
  });
}

/**
 * Renders plain text with normal paragraph wrapping, extra space after Enter
 * breaks, clickable autolinks, and @mentions.
 *
 * @param {Object} props
 * @param {React.ElementType} [props.as]
 * @param {string} [props.className]
 * @param {string} [props.linkClassName]
 * @param {string} [props.mentionClassName]
 * @param {Array<object>} [props.mentions]
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element | null}
 */
const FormattedBody = React.forwardRef(
  (
    {
      as: Comp = "div",
      className,
      linkClassName,
      mentionClassName,
      mentions,
      children,
      ...props
    },
    ref
  ) => {
    if (children == null || children === "") return null;

    const text = typeof children === "string" ? children : String(children);
    const lines = text.split("\n");
    const resolvedLinkClass = cn(FORMATTED_BODY_LINK_CLASS, linkClassName);
    const resolvedMentionClass = cn(
      FORMATTED_BODY_MENTION_CLASS,
      mentionClassName
    );
    const lookup = mentionLookup(mentions);

    return (
      <Comp
        ref={ref}
        className={cn("break-words leading-normal", className)}
        {...props}
      >
        {lines.map((line, index) => (
          <span
            key={index}
            className={cn("block", index < lines.length - 1 && "mb-0")}
          >
            {line.length > 0
              ? linkifyLine(
                  line,
                  resolvedLinkClass,
                  resolvedMentionClass,
                  lookup,
                  index
                )
              : "\u00A0"}
          </span>
        ))}
      </Comp>
    );
  }
);
FormattedBody.displayName = "FormattedBody";

export { FormattedBody };
